<page-heading
  [title]="isNew ? ('agents.openaiAssistantForm.createTitle' | translate) : ('agents.openaiAssistantForm.editTitle' | translate)"
  [description]="
    isNew
      ? ('agents.openaiAssistantForm.createDescription' | translate)
      : ('agents.openaiAssistantForm.editDescription' | translate)
  "
  [hasBreadcrumb]="true"
  [breadcrumb]="breadcrumb"
/>

<div class="tm-stepper mt-4 col-12 mx-auto">
  <div class="steps-wrapper position-relative">
    <div class="track w-100" aria-hidden="true"></div>
    <div
      class="progress-line"
      [style.width.%]="progressPct"
      aria-hidden="true"
    ></div>

    <div
      class="steps pt-3 d-flex flex-wrap justify-content-between mb-3 position-relative"
    >
      @for (step of steps; track $index;) {
        <div
          class="step d-flex align-items-center gap-2"
          [class.current]="$index + 1 === currentStep"
          [attr.aria-current]="$index + 1 === currentStep ? 'step' : null"
          (click)="goToStep($index); $event.stopPropagation()"
        >
          <div class="badge-num d-flex align-items-center justify-content-center">
            @if ($index < stepIndex()) {
              <i class="mdi mdi-check fw-bold" aria-hidden="true"></i>
            } @else {
              <span>{{ $index + 1 }}</span>
            }
          </div>
          <div class="text-truncate cursor-pointer">
            <div class="label text-truncate">{{ step.title }}</div>
            <small class="text-muted text-truncate d-block">
              {{ step.subtitle }}
            </small>
          </div>
        </div>
      }
    </div>
  </div>
</div>

<div class="row">
  <div class="col-12">
    <div class="card">
      <div class="card-body">
        @if (needsSetup) {
        <div class="alert alert-warning d-flex align-items-start gap-2" role="alert">
          <i class="mdi mdi-alert-outline fs-4"></i>
          <div>
            <strong>{{ 'agents.openaiAssistantForm.needsSetupTitle' | translate }}</strong>
            <div>{{ 'agents.openaiAssistantForm.needsSetupDescription' | translate }}</div>
          </div>
        </div>
        }

        <form [formGroup]="formGroup" (ngSubmit)="submitCurrentStep()">
          @switch (stepIndex()) {
            @case (0) {
              <div class="mb-3">
                <h4 class="header-title">{{ 'agents.openaiAssistantForm.basicInfoTitle' | translate }}</h4>
                <small class="text-muted">{{ 'agents.openaiAssistantForm.basicInfoSubtitle' | translate }}</small>
              </div>

              <div class="mb-3">
                <label for="assistantName" class="form-label fw-semibold">
                  {{ 'agents.openaiAssistantForm.nameLabel' | translate }} <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="assistantName"
                  class="form-control"
                  formControlName="name"
                  maxlength="250"
                  [ngClass]="displayFieldCss('name')"
                  [placeholder]="'agents.openaiAssistantForm.namePlaceholder' | translate"
                />
                @if (isFieldValid('name')) {
                <div class="invalid-feedback">{{ 'agents.openaiAssistantForm.nameRequired' | translate }}</div>
                }
              </div>

              <div class="mb-3">
                <label for="assistantModel" class="form-label fw-semibold">
                  {{ 'agents.openaiAssistantForm.modelLabel' | translate }} <span class="text-danger">*</span>
                </label>
                <select
                  id="assistantModel"
                  class="form-select"
                  formControlName="model"
                  [ngClass]="displayFieldCss('model')"
                >
                  <option value="">
                    {{ (modelsLoading ? 'agents.openaiAssistantForm.modelsLoading' : 'agents.openaiAssistantForm.selectOption') | translate }}
                  </option>
                  @for (model of models; track model.value) {
                  <option [value]="model.value">{{ model.label }}</option>
                  }
                </select>
                @if (isFieldValid('model')) {
                <div class="invalid-feedback">{{ 'agents.openaiAssistantForm.modelRequired' | translate }}</div>
                }
                <div class="form-text">
                  @if (modelsFromFallback) {
                    {{ 'agents.openaiAssistantForm.modelsFallbackHint' | translate }}
                  } @else {
                    {{ 'agents.openaiAssistantForm.modelsHint' | translate }}
                  }
                </div>
              </div>

              <div class="mb-3">
                <label for="assistantDescription" class="form-label fw-semibold">
                  {{ 'agents.openaiAssistantForm.descriptionLabel' | translate }}
                </label>
                <textarea
                  id="assistantDescription"
                  class="form-control"
                  formControlName="description"
                  maxlength="500"
                  rows="4"
                  [placeholder]="'agents.openaiAssistantForm.descriptionPlaceholder' | translate"
                ></textarea>
              </div>

              <div class="d-flex gap-2 mt-4">
                <button
                  type="button"
                  class="d-flex align-items-center gap-2 btn btn-outline-secondary rounded fw-semibold"
                  (click)="cancel()"
                >
                  {{ 'agents.openaiAssistantForm.cancelButton' | translate }}
                </button>
                <button
                  type="submit"
                  class="d-flex align-items-center gap-2 btn btn-primary ms-auto rounded fw-semibold"
                >
                  {{ 'agents.openaiAssistantForm.nextButton' | translate }}
                  <i class="mdi mdi-arrow-right-thin"></i>
                </button>
              </div>
            }

            @case (1) {
              <div class="mb-3">
                <h4 class="header-title">{{ 'agents.openaiAssistantForm.trainingTitle' | translate }}</h4>
                <small class="text-muted">{{ 'agents.openaiAssistantForm.trainingSubtitle' | translate }}</small>
              </div>

              <div class="mb-3">
                <label for="assistantInstructions" class="form-label fw-semibold">
                  {{ 'agents.openaiAssistantForm.instructionsLabel' | translate }} <span class="text-danger">*</span>
                </label>
                <textarea
                  id="assistantInstructions"
                  #instructionsInput
                  class="form-control instructions-textarea"
                  formControlName="instructions"
                  rows="10"
                  [ngClass]="displayFieldCss('instructions')"
                  [placeholder]="'agents.openaiAssistantForm.instructionsPlaceholder' | translate"
                ></textarea>
                @if (isFieldValid('instructions')) {
                <div class="invalid-feedback">{{ 'agents.openaiAssistantForm.instructionsRequired' | translate }}</div>
                }
                <div class="form-text text-end">
                  {{ instructionsInput.value?.length ?? 0 }} {{ 'agents.openaiAssistantForm.charactersLabel' | translate }}
                </div>
              </div>

              <div class="d-flex gap-2 mt-4">
                <button
                  type="button"
                  class="d-flex align-items-center gap-2 btn btn-outline-secondary rounded fw-semibold"
                  (click)="previousStep()"
                >
                  <i class="mdi mdi-arrow-left-thin"></i>
                  {{ 'agents.openaiAssistantForm.backButton' | translate }}
                </button>
                <button
                  type="submit"
                  class="d-flex align-items-center gap-2 btn btn-primary ms-auto rounded fw-semibold"
                  [disabled]="loading"
                >
                  @if (loading) {
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                  }
                  {{ 'agents.openaiAssistantForm.saveAndNextButton' | translate }}
                  <i class="mdi mdi-arrow-right-thin"></i>
                </button>
              </div>
            }

            @case (2) {
              <div class="mb-3">
                <h4 class="header-title">{{ 'agents.openaiAssistantForm.settingsTitle' | translate }}</h4>
                <small class="text-muted">{{ 'agents.openaiAssistantForm.settingsSubtitle' | translate }}</small>
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold">{{ 'agents.openaiAssistantForm.channelsLabel' | translate }}</label>
                <mat-select
                  class="form-select"
                  formControlName="channels"
                  multiple
                >
                  <mat-option value="" disabled>{{ 'agents.openaiAssistantForm.channelsPlaceholder' | translate }}</mat-option>
                  @for (channel of listChannel; track channel._id) {
                  <mat-option [value]="channel._id">{{ channel.name }}</mat-option>
                  }
                </mat-select>
              </div>

              <div class="d-flex gap-2 mt-4">
                <button
                  type="button"
                  class="d-flex align-items-center gap-2 btn btn-outline-secondary rounded fw-semibold"
                  (click)="previousStep()"
                >
                  <i class="mdi mdi-arrow-left-thin"></i>
                  {{ 'agents.openaiAssistantForm.backButton' | translate }}
                </button>
                <button
                  type="submit"
                  class="d-flex align-items-center gap-2 btn btn-success ms-auto rounded fw-semibold"
                  [disabled]="loading"
                >
                  @if (loading) {
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                  }
                  {{ 'agents.openaiAssistantForm.finishButton' | translate }}
                  <i class="mdi mdi-check"></i>
                </button>
              </div>
            }
          }
        </form>
      </div>
    </div>
  </div>
</div>
